import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import flagsmith from 'flagsmith';
import { StandardPage } from 'features/layouts';

import GetRoutes from '../../routes';
import { routes, Loader } from '../../routes/routeConfig';
import {
	isAuthenticated,
	fetchAppContext,
	requiresTermsAcceptance,
	getEmbargoPeriod,
	getContextEntitlements,
} from 'features/app/redux/context';
import MaintenancePage from './maintenance';
import { rtmSetupThunk } from '../rtmMessaging/redux';
import {
	getFeature,
	getInitialFlags,
	getInitialConfig,
	setLoadingFlags,
	getLoadingFlags,
	setBulkFlagsAndConfig,
	getConfig,
} from './redux/features';
import {
	isLoading,
	setTrendAccessId,
	setContextGroupAccessId,
	getClient,
} from 'features/app/redux/context';
import ErrorBoundary from './errorBoundary';
import { fetchGroupAccessRecords } from '../groups/setup/redux/thunks';
import { fetchGroupTrends } from 'features/groups/analysis/redux';
import JSON5 from 'json5';
import { setCurrentKeyStage } from 'features/groups/setup/redux';
import { setDefualtTrendSelectorValues } from 'features/groups/analysis/redux/thunks/setDefaultTrendSelectorValues';
// Theming
import { ThemeProvider, GlobalStyles, theme } from 'basecamp';
import { getTheme } from '../../utils/theming';
import { hasGroupAccess } from './redux/context';
import TermsAcceptance from 'features/app/termsAcceptance';
import {
	getPdfExportModalAcknowledgment,
	getPdfExportModalIsOpen,
} from '../app/redux/notificationModals';
import PDFExportModal from '../app/pdfExportModal';
import { useHistory, useLocation } from 'react-router-dom';
import { getGroupAccessRecords } from 'features/groups/setup/redux';
import { getAllGroupTrends, setGroupTrend } from 'features/groups/analysis/redux';
type Mode = 'CONNECT' | 'SUMMIT';

const App: React.FunctionComponent = () => {
	const dispatch = useDispatch();

	/**
	 ** Handle Selectors
	 */
	const maintenance = useSelector(getFeature('maintenance_mode'));
	const isGroup = useSelector(hasGroupAccess);
	// Check if the user is authenticated
	const isUserAuthenticated = useSelector(isAuthenticated);
	const termsAcceptance = useSelector(requiresTermsAcceptance);
	const loadingFlags = useSelector(getLoadingFlags);
	const entitlements = useSelector(getContextEntitlements);
	const embargoEnabled = useSelector(getFeature('embargo_feature'));
	const ks4EmbargoPeriod = useSelector(getConfig('early_access_ks4'));
	const ks5EmbargoPeriod = useSelector(getConfig('early_access_ks5'));

	const accessRecords = useSelector(getGroupAccessRecords);
	const trends = useSelector(getAllGroupTrends);
	const client = useSelector(getClient);
	const history = useHistory();
	const loading = useSelector(isLoading);
	const location = useLocation();
	const page = location.pathname.split('/').pop() as string;
	/**
	 * Handle Export modal
	 */
	//PDF MODAL SELECTORS
	const canViewNewExports = useSelector(getFeature('home_page_export_modal'));
	const showExportModalAck = useSelector(getPdfExportModalAcknowledgment);
	const showExportModalIsOpen = useSelector(getPdfExportModalIsOpen);
	const showModal = !canViewNewExports
		? false
		: showExportModalAck
		? false
		: showExportModalIsOpen
		? false
		: true;

	// TODO: handle cookies being disabled

	// Configure Flagsmith
	useEffect(() => {
		if (window.flagsmithAPIKey && window.flagsmithServer) {
			flagsmith
				.init({
					api: window.flagsmithServer,
					environmentID: window.flagsmithAPIKey,
					defaultFlags: getInitialFlags(),

					/*
						Prevent initial fetch of flags until the user has been identified
						Flagsmith will automatically get updated flags when flagsmith.identify() is called
						as part of connectctx call response handling

						If connectctx fails, we manually call flagsmith.getFlags to update the flags
					 */
					preventFetch: true,

					onChange: (_oldFlags, params) => {
						if (params.flagsChanged) {
							/*
							When remote config has changed, get all initial flags & config and loop over
							them, resetting each one in reduce with either the matching remote config,
							or the initial value (in the remote config has been removed)
						 */

							const flags = flagsmith.getAllFlags();

							dispatch(
								setBulkFlagsAndConfig({
									flags: Object.entries(getInitialFlags()).reduce((config, [id, { enabled }]) => {
										return {
											...config,
											[id]: flags[id]?.enabled ?? enabled,
										};
									}, {}),

									config: Object.entries(getInitialConfig()).reduce((config, [id, { value }]) => {
										return {
											...config,
											[id]: flags[id]?.value ?? value,
										};
									}, {}),
								})
							);
						}
					},
				})
				.then(() => {
					flagsmith.startListening(300000); // poll every 5 minutes
				})
				.catch((err) => {
					console.error(err.detail || err.message || err);
				});
		}

		dispatch(setLoadingFlags(false));
	}, []);

	useEffect(() => {
		dispatch(getEmbargoPeriod());
	}, [entitlements, embargoEnabled, ks4EmbargoPeriod, ks5EmbargoPeriod]);

	useEffect(() => {
		if (!isUserAuthenticated) {
			dispatch(fetchAppContext());
			//If the user is not authenticated, and we are half way through mfa push them back to the login
			if (page === 'mfaValidation') {
				history.push('/auth/login');
			}
		}
	}, [isUserAuthenticated]);

	let rtmSetupHasRun = false;
	useEffect(() => {
		if (!rtmSetupHasRun && isUserAuthenticated) {
			rtmSetupHasRun = true;
			dispatch(rtmSetupThunk());
		}
		return;
	}, [isUserAuthenticated]);

	const [mode, setMode] = useState<Mode>('CONNECT');

	const hasKeyStageBeenSet = localStorage.getItem('keyStageStorage');

	useEffect(() => {
		if (!!isGroup) {
			setMode('SUMMIT');
			if (!accessRecords || accessRecords.length === 0) {
				dispatch(fetchGroupAccessRecords());
			}

			dispatch(setTrendAccessId(undefined));
			//this is new - Im not sure if this is needed but with the new UI UX some of the logic in the trends is not working as expected
			dispatch(fetchGroupTrends());

			if (hasKeyStageBeenSet && JSON5.parse(hasKeyStageBeenSet).value) {
				const parsedKeyStage = JSON5.parse(hasKeyStageBeenSet);
				if (parsedKeyStage?.value) {
					dispatch(setContextGroupAccessId(parsedKeyStage.value));
					dispatch(setCurrentKeyStage(parsedKeyStage));
					dispatch(fetchGroupTrends());
				}
			}
			return;
		}

		setMode('CONNECT');
	}, [isGroup]);

	useEffect(() => {
		// if we're a MAT and we're not loading and there are trends
		if (client?.isContractOwner && !loading && trends.length > 0) {
			dispatch(setDefualtTrendSelectorValues());
			// pick the exam results trend and set that as the default on load
			const examTrend = trends.find((trend) => trend.name === 'Exam results');
			//if there is no trend id and has an exams result trend, set as default
			if (examTrend && !localStorage.getItem('summitGp')) {
				dispatch(setGroupTrend(examTrend.id));
			} else {
				//set summitgp as the default trend
				dispatch(setGroupTrend(Number(localStorage.getItem('summitGp'))));
			}
		}
	}, [loading, trends, client, accessRecords]);

	/**
	 ** Render Component and Routes
	 */
	let component = null;

	if (loadingFlags) {
		component = Loader();
	} else if (maintenance) {
		component = <MaintenancePage />;
	}

	return (
		<ThemeProvider getTheme={getTheme(mode, theme)}>
			<GlobalStyles />
			{/* Catch all components, such as loaders, V2 warning or maintenance */}
			{component}

			{termsAcceptance && <TermsAcceptance />}

			{showModal && isUserAuthenticated && <PDFExportModal />}

			{/* App */}
			{!component && isUserAuthenticated && (
				<ErrorBoundary>
					<StandardPage>
						<GetRoutes routes={routes} />
					</StandardPage>
				</ErrorBoundary>
			)}

			{/* Auth */}
			{!component && !isUserAuthenticated && <GetRoutes routes={routes} />}
		</ThemeProvider>
	);
};

export default App;
